/**
 * Skeletons.jsx  –  content-shaped skeleton loaders
 *
 * Usage:
 *   import { SalonCardSkeleton, GalleryGridSkeleton, ReviewCardSkeleton,
 *            SalonDetailSkeleton, SkeletonSwap } from '../components/common/Skeletons';
 *
 * SkeletonSwap — crossfade skeleton → content (200ms opacity transition):
 *   <SkeletonSwap loading={loading} skeleton={<SalonCardSkeleton />}>
 *     <SalonCard data={data} />
 *   </SkeletonSwap>
 */

import { useEffect, useRef, useState } from 'react';

/* ─── Base pulse ─────────────────────────────────────────────────────────── */
const pulse = {
  background: 'linear-gradient(90deg, rgba(220,215,240,0.35) 25%, rgba(220,215,240,0.60) 50%, rgba(220,215,240,0.35) 75%)',
  backgroundSize: '200% 100%',
  animation: 'skeletonPulse 1.6s ease-in-out infinite',
  borderRadius: '8px',
};

const styles = `
  @keyframes skeletonPulse {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
  @keyframes skeletonFadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
`;

/* ─── SkeletonSwap ────────────────────────────────────────────────────────
 *
 * Holds skeleton and content in the DOM at the same time.
 * When `loading` flips false the skeleton fades out and content fades in
 * over 200 ms — no layout shift, no snap.
 *
 * Props:
 *   loading   {boolean}    – true while data is fetching
 *   skeleton  {ReactNode}  – skeleton JSX to show while loading
 *   children  {ReactNode}  – real content to crossfade into
 *   style     {object}     – optional style on the wrapper div
 *   className {string}     – optional className on the wrapper div
 * ────────────────────────────────────────────────────────────────────────── */
export function SkeletonSwap({ loading, skeleton, children, style = {}, className = '' }) {
  // Keep skeleton mounted until the fade-out finishes so it doesn't vanish instantly
  const [skeletonVisible, setSkeletonVisible] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!loading) {
      // 200 ms matches the CSS transition – unmount skeleton after fade completes
      timerRef.current = setTimeout(() => setSkeletonVisible(false), 220);
     } else {
      // If loading re-triggers (e.g. filter change) bring skeleton back immediately.
      // Must stay synchronous here: a pending fade-out timer could otherwise
      // flip skeletonVisible false right after this branch re-shows it.
      clearTimeout(timerRef.current);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSkeletonVisible(true);
    }
    return () => clearTimeout(timerRef.current);
  }, [loading]);

  return (
    <>
      <style>{styles}</style>
      <div style={{ position: 'relative', ...style }} className={className}>
        {/* Skeleton layer */}
        {skeletonVisible && (
          <div
            style={{
              // When loading ends, fade the skeleton out
              opacity: loading ? 1 : 0,
              transition: 'opacity 200ms ease',
              // Once content is visible, skeleton must not capture pointer events
              pointerEvents: loading ? 'auto' : 'none',
              // Stack above content during the fade so shimmer doesn't bleed through
              position: loading ? 'relative' : 'absolute',
              inset: 0,
              zIndex: loading ? 'auto' : 1,
            }}
          >
            {skeleton}
          </div>
        )}

        {/* Content layer */}
        <div
          style={{
            opacity: loading ? 0 : 1,
            transition: 'opacity 200ms ease',
            // Invisible content must not trap clicks while skeleton is shown
            pointerEvents: loading ? 'none' : 'auto',
            // Sit behind the skeleton during the crossfade
            position: loading ? 'absolute' : 'relative',
            inset: 0,
            width: '100%',
          }}
          aria-hidden={loading}
        >
          {children}
        </div>
      </div>
    </>
  );
}

function Bone({ width = '100%', height = '14px', radius = '8px', style = {} }) {
  return (
    <div
      style={{
        ...pulse,
        width,
        height,
        borderRadius: radius,
        flexShrink: 0,
        ...style,
      }}
    />
  );
}

/* ─── Salon Card Skeleton (SearchResults) ────────────────────────────────── */
export function SalonCardSkeleton() {
  return (
    <>
      <style>{styles}</style>
      <div
        style={{
          background: '#fff',
          borderRadius: '18px',
          border: '1px solid rgba(230,228,245,0.8)',
          boxShadow: '0 2px 16px rgba(140,130,200,0.10)',
          overflow: 'hidden',
          display: 'flex',
          height: '160px',
        }}
      >
        {/* Image placeholder */}
        <div style={{ width: '160px', flexShrink: 0 }}>
          <Bone width="100%" height="100%" radius="0" />
        </div>
        {/* Body */}
        <div style={{ flex: 1, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Bone width="60px" height="18px" radius="5px" />
            <Bone width="50px" height="12px" radius="4px" />
          </div>
          <Bone width="70%" height="18px" />
          <Bone width="45%" height="12px" />
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <Bone width="100%" height="28px" radius="10px" />
            <Bone width="85%" height="28px" radius="10px" />
          </div>
        </div>
      </div>
    </>
  );
}

export function SalonCardSkeletonList({ count = 5 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {Array.from({ length: count }).map((_, i) => (
        <SalonCardSkeleton key={i} />
      ))}
    </div>
  );
}

/* ─── Gallery Grid Skeleton ──────────────────────────────────────────────── */
export function GalleryGridSkeleton({ count = 18 }) {
  // Vary heights to mimic masonry
  const heights = [200, 260, 180, 300, 220, 240];
  return (
    <>
      <style>{styles}</style>
      <div style={{ columns: '5 160px', gap: '1rem' }}>
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={i}
            style={{
              ...pulse,
              height: `${heights[i % heights.length]}px`,
              borderRadius: '24px',
              marginBottom: '1rem',
              breakInside: 'avoid',
            }}
          />
        ))}
      </div>
    </>
  );
}

/* ─── Review Card Skeleton ───────────────────────────────────────────────── */
export function ReviewCardSkeleton() {
  return (
    <>
      <style>{styles}</style>
      <div
        style={{
          background: '#fff',
          padding: '20px',
          borderRadius: '16px',
          border: '1px solid rgba(220,215,240,0.5)',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}
      >
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <Bone width="40px" height="40px" radius="50%" />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <Bone width="40%" height="13px" />
            <Bone width="28%" height="10px" />
          </div>
          <Bone width="80px" height="14px" />
        </div>
        <Bone width="100%" height="12px" />
        <Bone width="85%" height="12px" />
        <Bone width="60%" height="12px" />
      </div>
    </>
  );
}

/* ─── Salon Detail Hero Skeleton ─────────────────────────────────────────── */
export function SalonDetailSkeleton() {
  return (
    <>
      <style>{`
        ${styles}
        @media (max-width: 900px) {
          .sd-skeleton-grid { grid-template-columns: 1fr !important; }
          .sd-skeleton-sidebar { display: none !important; }
        }
      `}</style>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '16px', boxSizing: 'border-box', width: '100%' }}>
        <div className="sd-skeleton-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '18px' }}>
          {/* Left */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px', minWidth: 0 }}>
            {/* Photo carousel */}
            <Bone width="100%" height="260px" radius="18px" />
            {/* About card */}
            <div style={{ background: '#fff', borderRadius: '22px', padding: '22px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Bone width="40%" height="28px" />
              <Bone width="60%" height="14px" />
              <Bone width="100%" height="12px" />
              <Bone width="90%" height="12px" />
              <Bone width="75%" height="12px" />
            </div>
            {/* Services card */}
            <div style={{ background: '#fff', borderRadius: '22px', padding: '22px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Bone width="30%" height="22px" />
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {[80, 100, 70, 90].map((w, i) => <Bone key={i} width={`${w}px`} height="32px" radius="50px" />)}
              </div>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Bone width="50%" height="16px" />
                  <Bone width="60px" height="16px" />
                </div>
              ))}
            </div>
          </div>

          {/* Right sidebar — hidden on mobile */}
          <div className="sd-skeleton-sidebar" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ background: '#fff', borderRadius: '22px', padding: '28px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Bone width="50%" height="20px" />
              <Bone width="100%" height="80px" radius="14px" />
              <Bone width="100%" height="50px" radius="12px" />
            </div>
            <div style={{ background: '#fff', borderRadius: '22px', padding: '28px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <Bone width="60%" height="18px" />
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Bone width="40%" height="13px" />
                  <Bone width="35%" height="13px" />
                </div>
              ))}
            </div>
            <div style={{ background: '#fff', borderRadius: '22px', padding: '28px' }}>
              <Bone width="100%" height="170px" radius="14px" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ─── UserDashboard Stats Skeleton ───────────────────────────────────────── */
export function DashboardSkeleton() {
  return (
    <>
      <style>{styles}</style>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '14px' }}>
          {[1, 2, 3].map((i) => (
            <div key={i} style={{ background: '#fff', borderRadius: '18px', padding: '18px 22px', border: '1px solid #dce8ec', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <Bone width="40%" height="10px" />
              <Bone width="50%" height="28px" />
            </div>
          ))}
        </div>
        {/* Chart */}
        <div style={{ background: '#fff', borderRadius: '20px', padding: '22px', border: '1px solid #dce8ec', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Bone width="30%" height="18px" />
          <Bone width="100%" height="120px" radius="12px" />
        </div>
      </div>
    </>
  );
}

/* ─── GallerySingleView Skeleton ──────────────────────────────────────────── */
export function GallerySingleViewSkeleton() {
  return (
    <>
      <style>{styles}</style>
      <div style={{ maxWidth: '1200px', margin: '24px auto', padding: '0 20px', display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Bone width="100%" height="480px" radius="24px" />
            <Bone width="100%" height="80px" radius="16px" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <Bone width="100%" height="100px" radius="20px" />
            <Bone width="100%" height="180px" radius="20px" />
            <Bone width="100%" height="140px" radius="20px" />
          </div>
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 *  HOME PAGE SKELETONS — one per section, combined in HomePageSkeleton
 * ═══════════════════════════════════════════════════════════════════════════ */

const gradientBg = 'linear-gradient(135deg, #dde4f5 0%, #e8e4f5 40%, #d8dff0 100%)';

/* ─── SalonCarousel Skeleton ───────────────────────────────────────────── */
function SalonCarouselSkeleton() {
  return (
    <section style={{ padding: '64px 0', background: gradientBg, position: 'relative', overflow: 'hidden' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 32px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '36px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Bone width="90px" height="11px" radius="4px" />
            <Bone width="220px" height="26px" radius="6px" />
            <Bone width="160px" height="13px" radius="4px" />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Bone width="38px" height="38px" radius="12px" />
            <Bone width="38px" height="38px" radius="12px" />
          </div>
        </div>
        {/* Cards */}
        <div className="home-sk-salon-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
          {[0, 1, 2, 3].map(i => (
            <div key={i} style={{
              background: '#fff', borderRadius: '18px', overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.85)',
              boxShadow: '0 2px 16px rgba(140,130,200,0.10)',
            }}>
              <Bone width="100%" height="180px" radius="0" />
              <div style={{ padding: '14px 16px 18px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <Bone width="75%" height="14px" />
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Bone width="11px" height="11px" radius="50%" />
                  <Bone width="55%" height="11px" />
                </div>
                <Bone width="40%" height="11px" />
                <div style={{ marginTop: '6px', height: '2px', width: '28px', borderRadius: '99px', background: 'rgba(167,139,220,0.2)' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── BenefitsSection Skeleton ─────────────────────────────────────────── */
function BenefitsSkeleton() {
  return (
    <section style={{ padding: '52px 0 20px', background: gradientBg }}>
      <div style={{ maxWidth: '920px', margin: '0 auto', padding: '0 24px' }}>
        {/* Heading */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '32px' }}>
          <div style={{ height: '1px', flex: '1', maxWidth: '60px', background: 'rgba(124,92,191,0.15)' }} />
          <Bone width="200px" height="24px" radius="6px" />
          <div style={{ height: '1px', flex: '1', maxWidth: '60px', background: 'rgba(124,92,191,0.15)' }} />
        </div>
        {/* Cards */}
        <div className="home-sk-benefits-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{
              background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.7)',
              borderRadius: '14px', padding: '22px 20px 20px',
              display: 'flex', flexDirection: 'column', gap: '0',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <Bone width="38px" height="38px" radius="10px" />
                <Bone width="70px" height="10px" radius="4px" />
              </div>
              <Bone width="80%" height="15px" style={{ marginBottom: '8px' }} />
              <Bone width="100%" height="12px" style={{ marginBottom: '4px' }} />
              <Bone width="70%" height="12px" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── VisionCarousel Skeleton ──────────────────────────────────────────── */
function VisionCarouselSkeleton() {
  return (
    <section style={{ padding: '64px 0', background: gradientBg }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 32px 36px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Bone width="80px" height="11px" radius="4px" />
            <Bone width="240px" height="26px" radius="6px" />
            <Bone width="180px" height="13px" radius="4px" />
          </div>
          <Bone width="140px" height="40px" radius="99px" />
        </div>
      </div>
      {/* Image grid */}
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 12px' }}>
        <div className="home-sk-vision-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px' }}>
          {[0, 1, 2, 3, 4, 5].map(i => (
            <div key={i} style={{
              borderRadius: '16px', overflow: 'hidden',
              boxShadow: '0 2px 16px rgba(140,130,200,0.10)',
              background: '#fff',
            }}>
              <Bone width="100%" height="240px" radius="0" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── CityLinksSection Skeleton ────────────────────────────────────────── */
function CityLinksSkeleton() {
  return (
    <section style={{
      padding: '90px 0', background: '#F4F6FB',
      borderTop: '1px solid rgba(180,180,220,0.18)',
    }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 32px' }}>
        {/* Heading */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', marginBottom: '56px' }}>
          <Bone width="100px" height="11px" radius="4px" />
          <Bone width="280px" height="30px" radius="6px" />
        </div>
        {/* Glass card */}
        <div style={{
          background: 'rgba(255,255,255,0.70)', border: '1px solid rgba(255,255,255,0.90)',
          borderRadius: '24px', boxShadow: '0 8px 32px rgba(100,100,160,0.08)',
          padding: '40px 36px',
        }}>
          <div className="home-sk-city-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '32px' }}>
            {[0, 1, 2, 3, 4].map(col => (
              <div key={col} style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                {/* Column header */}
                <div style={{ marginBottom: '20px' }}>
                  {col === 0 ? (
                    <Bone width="110px" height="22px" radius="99px" />
                  ) : (
                    <>
                      <Bone width="80px" height="14px" radius="4px" />
                      <div style={{ marginTop: '6px', width: '28px', height: '2px', borderRadius: '99px', background: 'rgba(108,142,239,0.3)' }} />
                    </>
                  )}
                </div>
                {/* Links */}
                {[0, 1, 2, 3].map(j => (
                  <Bone key={j} width={`${60 + (j % 3) * 12}%`} height="13px" style={{ marginBottom: '10px' }} />
                ))}
                <Bone width="90px" height="12px" radius="4px" style={{ marginTop: '6px' }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Combined Home Page Skeleton ──────────────────────────────────────── */
export function HomePageSkeleton() {
  return (
    <>
      <style>{`
        ${styles}
        @media (max-width: 900px) {
          .home-sk-salon-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .home-sk-city-grid  { grid-template-columns: repeat(3, 1fr) !important; }
        }
        @media (max-width: 768px) {
          .home-sk-vision-grid { grid-template-columns: repeat(3, 1fr) !important; }
        }
        @media (max-width: 700px) {
          .home-sk-benefits-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 600px) {
          .home-sk-salon-grid  { grid-template-columns: 1fr !important; }
          .home-sk-city-grid   { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 480px) {
          .home-sk-vision-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 380px) {
          .home-sk-city-grid   { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <SalonCarouselSkeleton />
      <BenefitsSkeleton />
      <VisionCarouselSkeleton />
      <CityLinksSkeleton />
    </>
  );
}